import { useEffect, useRef } from "react";
import { usePlayerStore } from "./playerStore.js";
import { currentChannelKey, findTrack, useTrackMemory, viewerChoiceAt } from "./trackMemory.js";

/** How many one-second looks a tune gets at putting the choice back. */
const RESTORE_LOOKS = 8;

/**
 * When a live channel starts playing, put back the sound and subtitles the viewer chose on it
 * last time (owner, 2026-10-12). Once per tune. Never records anything itself.
 */
export function useTrackMemoryRestore(): void {
  const status = usePlayerStore((state) => state.status);
  const url = usePlayerStore((state) => state.source?.url ?? null);
  const done = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "playing" || !url || done.current === url) return;
    const key = currentChannelKey();
    if (!key) return;
    const saved = useTrackMemory.getState().byKey[key];
    done.current = url;
    if (!saved) return;
    const player = usePlayerStore.getState().player;
    if (!player?.tracks || !player.selectTrack) return;

    // A stream lists its tracks a moment after the first frame, and the engine may apply its own
    // idea (the subtitles that were on for the last channel) after that. So look once a second
    // for a few seconds, put the choice back, and stop as soon as it holds, the viewer changes
    // anything themselves, or the channel changes.
    const startedAt = Date.now();
    const apply = (tries: number) => {
      if (viewerChoiceAt() >= startedAt) return;
      if (usePlayerStore.getState().source?.url !== url) return;
      void player
        .tracks!()
        .then((tracks) => {
          if (viewerChoiceAt() >= startedAt) return;
          let settled = tracks.length > 0;
          if (saved.audio) {
            const track = findTrack(tracks, "audio", saved.audio);
            if (track && !track.selected) {
              void player.selectTrack!("audio", track.id);
              settled = false;
            }
          }
          if (saved.text === "off") {
            if (tracks.some((track) => track.kind === "text" && track.selected)) {
              void player.selectTrack!("text", null);
              settled = false;
            }
          } else if (saved.text) {
            const track = findTrack(tracks, "text", saved.text);
            if (track && !track.selected) {
              void player.selectTrack!("text", track.id);
              settled = false;
            }
          }
          // Settled once everything lines up and it has held for a second look.
          if (tries > 0 && !(settled && tries <= RESTORE_LOOKS - 2)) setTimeout(() => apply(tries - 1), 1000);
        })
        .catch(() => undefined);
    };
    apply(RESTORE_LOOKS);
  }, [status, url]);
}
